import { Target, Bell, Sparkles, Leaf, Heart, Shield } from "lucide-react";

const principles = [
  {
    icon: Target,
    title: "Blue for focus",
    body: "Signals clarity, trust, and forward movement.",
    bg: "#DBEAFE",
    accent: "#2563EB",
  },
  {
    icon: Bell,
    title: "Amber instead of red",
    body: "Gentle reminders that nudge, not alarm.",
    bg: "#FEF3C7",
    accent: "#F59E0B",
  },
  {
    icon: Sparkles,
    title: "Purple for AI support",
    body: "Creative, supportive, and non-judgmental.",
    bg: "#EDE9FE",
    accent: "#7C3AED",
  },
  {
    icon: Leaf,
    title: "Green for calm progress",
    body: "Positive feedback that reinforces momentum.",
    bg: "#DCFCE7",
    accent: "#22C55E",
  },
  {
    icon: Heart,
    title: "Coral for soft urgency",
    body: "Warm, human cues without pressure.",
    bg: "#FFE4DE",
    accent: "#F9735B",
  },
];

export default function AdhdSafeUsage() {
  return (
    <section
      id="adhd-safe"
      data-testid="adhd-safe-section"
      className="px-6 lg:px-10 py-24 lg:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="fade-up max-w-3xl">
          <p className="font-label text-sm tracking-[0.18em] uppercase font-semibold text-[#7C3AED]">
            ADHD-safe color system
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold leading-[1.05] text-[#0F172A]">
            Every color carries a feeling — on purpose.
          </h2>
          <p className="mt-4 text-lg text-[#475569] leading-relaxed max-w-2xl">
            Mindrail&apos;s palette is built around how ADHD brains read
            color. No red panic. No surveillance grays. Just five clear
            roles, used consistently across the product.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {principles.map((p, i) => (
            <div
              key={i}
              data-testid={`safe-principle-${i}`}
              className="fade-up rounded-3xl border border-[#E2E8F0] bg-white p-6 hover:-translate-y-1 transition-transform duration-500"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span
                className="inline-flex items-center justify-center w-11 h-11 rounded-2xl"
                style={{ background: p.bg }}
              >
                <p.icon
                  className="w-5 h-5"
                  strokeWidth={1.75}
                  style={{ color: p.accent }}
                />
              </span>
              <h3
                className="mt-5 font-display text-lg font-semibold"
                style={{ color: p.accent }}
              >
                {p.title}
              </h3>
              <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">
                {p.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 fade-up">
          <div className="rounded-3xl bg-gradient-to-r from-[#DBEAFE] via-[#EDE9FE] to-[#FFE4DE] p-[1px]">
            <div className="rounded-3xl bg-white px-7 py-6 flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#EDE9FE] shrink-0">
                <Shield
                  className="w-5 h-5 text-[#7C3AED]"
                  strokeWidth={1.75}
                />
              </span>
              <p className="text-base text-[#0F172A] leading-relaxed">
                Mindrail is designed to{" "}
                <span className="font-semibold">reduce friction</span>, lower
                overwhelm, and help you feel safe and in control.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
